import React, { useState, useEffect } from 'react'
import { Plus, Loader2, Zap, AlertCircle, X, Package, Copy } from 'lucide-react'
import { Drawer } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import useActivationStore from '../../stores/activationStore'
import useActivationTypeStore from '../../stores/activationTypeStore'
import usePartnerStore from '../../stores/partnerStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import { Activation, ActivationStatus } from '../../types/activation'
import { Asset } from '../../types/asset'
import { assetService } from '../../services/assetService'
import { activationService } from '../../services/activationService'
import CPMSlider from '../planner/CPMSlider'

interface AddActivationDrawerProps {
  isOpen: boolean
  onClose: () => void
  /** Optional activation to duplicate from (pre-populates fields except name, description, dates, EMV values) */
  duplicateFrom?: Activation | null
}

const AddActivationDrawer: React.FC<AddActivationDrawerProps> = ({ isOpen, onClose, duplicateFrom }) => {
  const { showSuccess, showError } = useToast()
  const { createActivation, setActivationAssets, loadActivations } = useActivationStore()
  const { activationTypes, loadActivationTypes } = useActivationTypeStore()
  const { partners, loadPartners } = usePartnerStore()
  const { selectedCustomer } = useGlobalCustomerStore()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [partnerId, setPartnerId] = useState('')
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([])
  const [activationTypeId, setActivationTypeId] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [dealValue, setDealValue] = useState('')
  const [emvTargetValue, setEmvTargetValue] = useState('')
  const [notes, setNotes] = useState('')
  const [status, setStatus] = useState<ActivationStatus>('Pending')
  const [isCreating, setIsCreating] = useState(false)

  // CPM Multipliers (stored as rate per engagement, e.g., 0.004 = $4.00 CPM)
  const [viewsCpm, setViewsCpm] = useState(0.004)
  const [likesCpm, setLikesCpm] = useState(0.006)
  const [commentsCpm, setCommentsCpm] = useState(0.008)
  const [sharesCpm, setSharesCpm] = useState(0.01)

  // Assets state
  const [assets, setAssets] = useState<Asset[]>([])
  const [isLoadingAssets, setIsLoadingAssets] = useState(false)

  useEffect(() => {
    if (isOpen && selectedCustomer?.id) {
      loadActivationTypes(String(selectedCustomer.id), true)
      loadPartners(String(selectedCustomer.id), undefined, 'Active', undefined, undefined, 1, 100)
      loadAssets()
    }
  }, [isOpen, selectedCustomer?.id, loadActivationTypes, loadPartners])

  // Populate form when duplicating from an existing activation
  useEffect(() => {
    const populateFromDuplicate = async () => {
      if (!isOpen || !duplicateFrom || !selectedCustomer?.id) return

      // Set basic fields (excluding name, description, dates)
      setPartnerId(duplicateFrom.partner_id || '')
      setActivationTypeId(duplicateFrom.activation_type_id || '')
      setDealValue(duplicateFrom.deal_value?.toString() || '')
      setEmvTargetValue(duplicateFrom.emv_target_value?.toString() || '')
      setNotes(duplicateFrom.notes || '')
      setStatus('Pending') // Always start as Pending

      // Load CPM values from metadata
      setViewsCpm(duplicateFrom.metadata?.views_cpm ?? 0.004)
      setLikesCpm(duplicateFrom.metadata?.likes_cpm ?? 0.006)
      setCommentsCpm(duplicateFrom.metadata?.comments_cpm ?? 0.008)
      setSharesCpm(duplicateFrom.metadata?.shares_cpm ?? 0.01)

      // Fetch full activation to get asset IDs
      try {
        const fullActivation = await activationService.getActivation(
          duplicateFrom.id,
          String(selectedCustomer.id)
        )
        const assetIds = fullActivation.activation_assets
          ?.filter((aa) => aa.asset_id)
          .map((aa) => aa.asset_id) || []
        setSelectedAssetIds(assetIds)
      } catch (error) {
        console.error('Failed to fetch activation assets for duplication:', error)
      }
    }

    populateFromDuplicate()
  }, [isOpen, duplicateFrom, selectedCustomer?.id])

  const loadAssets = async () => {
    if (!selectedCustomer?.id) return
    setIsLoadingAssets(true)
    try {
      const response = await assetService.getAssets(
        String(selectedCustomer.id),
        'Active',
        undefined,
        undefined,
        undefined,
        1,
        100
      )
      setAssets(response.data)
    } catch (error) {
      console.error('Failed to load assets:', error)
    } finally {
      setIsLoadingAssets(false)
    }
  }

  const resetForm = () => {
    setName('')
    setDescription('')
    setPartnerId('')
    setSelectedAssetIds([])
    setActivationTypeId('')
    setStartDate('')
    setEndDate('')
    setDealValue('')
    setEmvTargetValue('')
    setNotes('')
    setStatus('Pending')
    setViewsCpm(0.004)
    setLikesCpm(0.006)
    setCommentsCpm(0.008)
    setSharesCpm(0.01)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleAssetToggle = (assetId: string) => {
    setSelectedAssetIds((prev) =>
      prev.includes(assetId)
        ? prev.filter((id) => id !== assetId)
        : [...prev, assetId]
    )
  }

  const handleRemoveAsset = (assetId: string) => {
    setSelectedAssetIds((prev) => prev.filter((id) => id !== assetId))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedCustomer?.id) {
      showError('Error', 'Please select a customer first')
      return
    }

    if (!name.trim()) {
      showError('Validation Error', 'Activation name is required')
      return
    }

    if (name.length > 255) {
      showError('Validation Error', 'Activation name must be 255 characters or less')
      return
    }

    if (!partnerId) {
      showError('Validation Error', 'Partner is required')
      return
    }

    // Validate required fields for Active status
    if (status === 'Active') {
      const missingFields: string[] = []
      if (selectedAssetIds.length === 0) missingFields.push('At least one Asset')
      if (!activationTypeId) missingFields.push('Activation Type')
      if (!startDate) missingFields.push('Start Date')
      if (!endDate) missingFields.push('End Date')

      if (missingFields.length > 0) {
        showError(
          'Validation Error',
          `To set status to Active, the following are required: ${missingFields.join(', ')}`
        )
        return
      }

      // Check if start date is in the past
      const validationToday = new Date()
      validationToday.setHours(0, 0, 0, 0)
      const selectedStartDate = new Date(startDate)
      if (selectedStartDate < validationToday) {
        showError(
          'Invalid Start Date',
          'Cannot activate an activation with a start date in the past. Please update the start date.'
        )
        return
      }
    }

    setIsCreating(true)

    try {
      const newActivation = await createActivation(String(selectedCustomer.id), {
        name: name.trim(),
        description: description.trim() || null,
        partner_id: partnerId,
        activation_type_id: activationTypeId || null,
        start_date: startDate || null,
        end_date: endDate || null,
        deal_value: dealValue ? parseFloat(dealValue) : null,
        emv_target_value: emvTargetValue ? parseFloat(emvTargetValue) : null,
        notes: notes.trim() || null,
        status,
        metadata: {
          views_cpm: viewsCpm,
          likes_cpm: likesCpm,
          comments_cpm: commentsCpm,
          shares_cpm: sharesCpm
        }
      })

      // If assets were selected, associate them with the activation
      if (selectedAssetIds.length > 0) {
        await setActivationAssets(
          newActivation.id,
          String(selectedCustomer.id),
          selectedAssetIds
        )
      }

      showSuccess('Activation Created', `"${newActivation.name}" has been created successfully`)

      await loadActivations(String(selectedCustomer.id))

      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Create activation error:', error)
      showError(
        'Failed to create activation',
        errorMessage.includes('already exists')
          ? 'An activation with this name already exists'
          : errorMessage
      )
    } finally {
      setIsCreating(false)
    }
  }

  const activePartners = partners
    .filter((p) => p.status === 'Active')
    .sort((a, b) => a.name.localeCompare(b.name))

  // Show warning if trying to set Active without required fields
  const showActiveWarning = status === 'Active' && (
    selectedAssetIds.length === 0 || !activationTypeId || !startDate || !endDate
  )

  // Check if trying to activate with a past start date
  const formStartDate = startDate ? new Date(startDate) : null
  const todayForValidation = new Date()
  todayForValidation.setHours(0, 0, 0, 0)
  const showPastStartDateWarning = status === 'Active' && formStartDate !== null && formStartDate < todayForValidation

  // Get selected assets for display
  const selectedAssets = assets.filter((a) => selectedAssetIds.includes(a.id))
  const availableAssets = assets
    .filter((a) => !selectedAssetIds.includes(a.id))
    .sort((a, b) => a.name.localeCompare(b.name))

  const isDuplicating = Boolean(duplicateFrom)

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      title={isDuplicating ? 'Duplicate Activation' : 'Add Activation'}
      description={isDuplicating ? 'Create a copy of an existing activation' : 'Create a new campaign activation'}
      icon={isDuplicating ? <Copy className="h-5 w-5" /> : <Zap className="h-5 w-5" />}
      width="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Assets Multi-Select - At Top */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Assets {status === 'Active' && <span className="text-red-500">*</span>}
          </label>

          {/* Selected Assets */}
          {selectedAssets.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {selectedAssets.map((asset) => (
                <span
                  key={asset.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200"
                >
                  <Package className="h-3 w-3" />
                  {asset.name}
                  <button
                    type="button"
                    onClick={() => handleRemoveAsset(asset.id)}
                    disabled={isCreating}
                    className="hover:text-blue-900 disabled:opacity-50"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Asset Selector */}
          <select
            value=""
            onChange={(e) => e.target.value && handleAssetToggle(e.target.value)}
            disabled={isCreating || isLoadingAssets || availableAssets.length === 0}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
          >
            <option value="">
              {isLoadingAssets
                ? 'Loading assets...'
                : availableAssets.length === 0
                  ? 'No more assets available'
                  : 'Select asset(s)'}
            </option>
            {availableAssets.map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="name" className="block text-xs font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Q1 2026 Content Campaign"
            maxLength={255}
            disabled={isCreating}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-xs font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe this activation"
            rows={2}
            disabled={isCreating}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed resize-none"
          />
        </div>

        {/* Partner and Activation Type - Same Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="partner" className="block text-xs font-medium text-gray-700 mb-1">
              Partner <span className="text-red-500">*</span>
            </label>
            <select
              id="partner"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              disabled={isCreating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="">Select partner</option>
              {activePartners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="type" className="block text-xs font-medium text-gray-700 mb-1">
              Activation Type {status === 'Active' && <span className="text-red-500">*</span>}
            </label>
            <select
              id="type"
              value={activationTypeId}
              onChange={(e) => setActivationTypeId(e.target.value)}
              disabled={isCreating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="">Select type</option>
              {activationTypes
                .filter((at) => at.is_active)
                .map((at) => (
                  <option key={at.id} value={at.id}>
                    {at.name}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="startDate" className="block text-xs font-medium text-gray-700 mb-1">
              Start Date {status === 'Active' && <span className="text-red-500">*</span>}
            </label>
            <input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={isCreating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-xs font-medium text-gray-700 mb-1">
              End Date {status === 'Active' && <span className="text-red-500">*</span>}
            </label>
            <input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={isCreating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="dealValue" className="block text-xs font-medium text-gray-700 mb-1">
              Deal Value
            </label>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">
                $
              </span>
              <input
                id="dealValue"
                type="number"
                step="0.01"
                min="0"
                value={dealValue}
                onChange={(e) => setDealValue(e.target.value)}
                placeholder="0.00"
                disabled={isCreating}
                className="w-full pl-5 pr-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div>
            <label htmlFor="emvTargetValue" className="block text-xs font-medium text-gray-700 mb-1">
              EMV Target
            </label>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">
                $
              </span>
              <input
                id="emvTargetValue"
                type="number"
                step="0.01"
                min="0"
                value={emvTargetValue}
                onChange={(e) => setEmvTargetValue(e.target.value)}
                placeholder="0.00"
                disabled={isCreating}
                className="w-full pl-5 pr-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        {/* CPM Multipliers */}
        <div className="border-t border-gray-200 pt-3">
          <h4 className="text-xs font-medium text-gray-700 mb-2">EMV Multipliers</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <CPMSlider
              label="Views"
              value={viewsCpm}
              onChange={setViewsCpm}
              min={0}
              max={0.03}
              step={0.0005}
              disabled={isCreating}
            />
            <CPMSlider
              label="Likes"
              value={likesCpm}
              onChange={setLikesCpm}
              min={0}
              max={0.03}
              step={0.0005}
              disabled={isCreating}
            />
            <CPMSlider
              label="Comments"
              value={commentsCpm}
              onChange={setCommentsCpm}
              min={0}
              max={0.03}
              step={0.0005}
              disabled={isCreating}
            />
            <CPMSlider
              label="Shares"
              value={sharesCpm}
              onChange={setSharesCpm}
              min={0}
              max={0.03}
              step={0.0005}
              disabled={isCreating}
            />
          </div>
        </div>

        <div>
          <label htmlFor="notes" className="block text-xs font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes..."
            rows={2}
            disabled={isCreating}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed resize-none"
          />
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="block text-xs font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value as ActivationStatus)}
            disabled={isCreating}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
          >
            <option value="Pending">Pending</option>
            <option value="Active">Active</option>
          </select>
        </div>

        {/* Warning for Active status without required fields */}
        {showActiveWarning && (
          <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              To set status to Active: At least one Asset, Activation Type, Start Date, and End Date are required.
            </p>
          </div>
        )}

        {showPastStartDateWarning && !showActiveWarning && (
          <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              Cannot activate an activation with a start date in the past. Please update the start date.
            </p>
          </div>
        )}

        <div className="flex space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isCreating}
            className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isCreating || !name.trim() || !partnerId || showActiveWarning || showPastStartDateWarning}
            className="flex-1 inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Create Activation
              </>
            )}
          </button>
        </div>
      </form>
    </Drawer>
  )
}

export default AddActivationDrawer
